import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config/api';
import { bindStorefrontRefresh } from '../../utils/storefrontRefresh';
import './SpiritualCollection.css';
import rudrakshImg from '../Assets/Rudraksh.jpeg';
import blowingConchImg from '../Assets/conch(blowing).png';
import rightHandConchImg from '../Assets/rshank.jpg';
import shreefalImg from '../Assets/shreefall.jpg';

const CARD_STYLES = [
  {
    gradient: 'linear-gradient(135deg, #5c2a0a 0%, #a0521e 45%, #c47a35 100%)',
    glow: 'rgba(196, 122, 53, 0.4)',
    tint: 'rgba(255, 214, 170, 0.22)',
  },
  {
    gradient: 'linear-gradient(135deg, #1a3a2e 0%, #2d6b50 45%, #4a9e7a 100%)',
    glow: 'rgba(74, 158, 122, 0.4)',
    tint: 'rgba(198, 255, 228, 0.18)',
  },
  {
    gradient: 'linear-gradient(135deg, #1a1a4e 0%, #2e2e8a 45%, #5050c8 100%)',
    glow: 'rgba(80, 80, 200, 0.4)',
    tint: 'rgba(214, 216, 255, 0.2)',
  },
  {
    gradient: 'linear-gradient(135deg, #1e3a1a 0%, #3a6b2d 45%, #5a9e48 100%)',
    glow: 'rgba(90, 158, 72, 0.4)',
    tint: 'rgba(229, 255, 188, 0.18)',
  },
];

const FALLBACK_ITEMS = [
  {
    key: 'rudraksh',
    badgeText: 'Sacred Beads',
    title: 'Rudraksh',
    description: 'Sacred Beads of Lord Shiva',
    redirectLink: '/store?material=RUDRAKSH',
    imageUrl: rudrakshImg,
    imageAlt: 'Rudraksh beads',
    order: 1,
    isVisible: true,
  },
  {
    key: 'blowing-conch',
    badgeText: 'Temple Sound',
    title: 'Blowing Conch',
    description: 'Shankh - Divine Sound',
    redirectLink: '/store?material=BLOWING%20COANCH%20(SHANKH)',
    imageUrl: blowingConchImg,
    imageAlt: 'Blowing conch shell',
    order: 2,
    isVisible: true,
  },
  {
    key: 'right-hand-conch',
    badgeText: 'Rare Blessing',
    title: 'Right-Hand Conch',
    description: 'Dakshinavarti Shankh - Rare and Auspicious',
    redirectLink: '/store?material=RIGHT%20HAND%20COANCH%20(DAKSHINAVARTI)',
    imageUrl: rightHandConchImg,
    imageAlt: 'Right-hand conch',
    order: 3,
    isVisible: true,
  },
  {
    key: 'ekakshi-shreefal',
    badgeText: 'Prosperity Symbol',
    title: 'Ekakshi Shreefal',
    description: 'One Eye Coconut - Symbol of Prosperity',
    redirectLink: '/one-eyed-coconut',
    imageUrl: shreefalImg,
    imageAlt: 'Ekakshi shreefal coconut',
    order: 4,
    isVisible: true,
  },
];

const FALLBACK_SECTION = {
  heading: 'Spiritual Collection',
  subtitle:
    'Discover our handpicked sacred treasures - each piece carrying divine energy, positive vibrations and centuries of spiritual significance.',
  backgroundColor: '#241238',
  backgroundImage: '',
  cards: FALLBACK_ITEMS,
};

const getFallbackCard = (card = {}, index = 0) =>
  FALLBACK_ITEMS.find((fallbackCard) => fallbackCard.key === card.key)
  || FALLBACK_ITEMS.find((fallbackCard) => fallbackCard.title === card.title)
  || FALLBACK_ITEMS[index % FALLBACK_ITEMS.length]
  || {};

const normalizeCards = (cards = []) => {
  const sourceCards = Array.isArray(cards) && cards.length ? cards : FALLBACK_ITEMS;

  return sourceCards
    .map((card, index) => {
      const fallbackCard = getFallbackCard(card, index);

      return {
        ...fallbackCard,
        ...card,
        imageUrl: card.imageUrl || fallbackCard.imageUrl,
        imageAlt: card.title || fallbackCard.imageAlt || 'Spiritual collection product',
        order: Number(card.order || fallbackCard.order || index + 1),
        isVisible: card.isVisible !== false,
      };
    })
    .filter((card) => card.isVisible)
    .sort((leftCard, rightCard) => leftCard.order - rightCard.order);
};

const SpiritualCollection = () => {
  const navigate = useNavigate();
  const [section, setSection] = useState(FALLBACK_SECTION);

  const fetchSpiritualCollection = async () => {
    try {
      const response = await fetch(`${API_BASE}/spiritual-collection`, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch spiritual collection');
      }

      const data = await response.json();
      setSection({
        heading: data.heading || FALLBACK_SECTION.heading,
        subtitle: data.subtitle || FALLBACK_SECTION.subtitle,
        backgroundColor: data.backgroundColor || FALLBACK_SECTION.backgroundColor,
        backgroundImage: data.backgroundImage || '',
        cards: normalizeCards(data.cards),
      });
    } catch (error) {
      console.error('Failed to load spiritual collection:', error);
      setSection(FALLBACK_SECTION);
    }
  };

  useEffect(() => {
    fetchSpiritualCollection();
  }, []);

  useEffect(() => (
    bindStorefrontRefresh(fetchSpiritualCollection, {
      scopes: ['all', 'homepage'],
      minIntervalMs: 1000,
    })
  ), []);

  const visibleCards = useMemo(() => normalizeCards(section.cards), [section.cards]);
  const sectionStyle = {
    '--sc-background-color': section.backgroundColor || FALLBACK_SECTION.backgroundColor,
    ...(section.backgroundImage
      ? { '--sc-background-image': `url("${section.backgroundImage}")` }
      : {}),
  };

  const handleCardClick = (redirectLink = '') => {
    const nextLink = String(redirectLink || '').trim();

    if (!nextLink) {
      return;
    }

    if (/^https?:\/\//i.test(nextLink)) {
      window.location.href = nextLink;
      return;
    }

    navigate(nextLink.startsWith('/') ? nextLink : `/${nextLink}`);
  };

  return (
    <section className={`sc-section ${section.backgroundImage ? 'has-background-image' : ''}`} style={sectionStyle}>
      <div className="sc-bg-circle sc-bg-1" />
      <div className="sc-bg-circle sc-bg-2" />

      <div className="sc-inner">
        <div className="sc-header">
          <span className="sc-tag">Sacred and Divine</span>
          <h2 className="sc-heading">{section.heading}</h2>
          <p className="sc-desc">{section.subtitle}</p>
        </div>

        <div className="sc-grid">
          {visibleCards.map((item, index) => {
            const cardStyle = CARD_STYLES[index % CARD_STYLES.length];

            return (
              <div
                key={item._id || item.key || `${item.title}-${index}`}
                className="sc-banner-card"
                style={{
                  background: cardStyle.gradient,
                  '--glow': cardStyle.glow,
                  '--card-tint': cardStyle.tint,
                }}
                onClick={() => handleCardClick(item.redirectLink)}
              >
                <div className="sc-ring sc-ring-1" />
                <div className="sc-ring sc-ring-2" />
                <div className="sc-card-shine" />
                <div className="sc-card-orbit" />

                <div className="sc-banner-media">
                  <div className="sc-banner-aura" />
                  <div className="sc-banner-image-wrap">
                    <img
                      src={item.imageUrl}
                      alt={item.imageAlt}
                      className="sc-banner-image"
                      loading="lazy"
                    />
                  </div>
                </div>

                <div className="sc-banner-body">
                  <span className="sc-banner-tag">{item.badgeText}</span>
                  <h3 className="sc-banner-title">{item.title}</h3>
                  <p className="sc-banner-sub">{item.description}</p>
                  <button className="sc-banner-btn" type="button">
                    Explore Collection
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SpiritualCollection;
